import React, { useEffect, useRef } from 'react';

interface StreakLineAnimationProps {
  variant: 'wins' | 'losses';
  isDarkMode: boolean;
}

const PALETTES = {
  wins: {
    line: ['#34d399', '#10b981', '#22c55e'],
    darkFill: 'rgba(16, 185, 129, 0.24)',
    lightFill: 'rgba(5, 150, 105, 0.15)',
    darkGrid: 'rgba(52, 211, 153, 0.08)',
    lightGrid: 'rgba(5, 150, 105, 0.07)',
  },
  losses: {
    line: ['#fb7185', '#ef4444', '#dc2626'],
    darkFill: 'rgba(239, 68, 68, 0.24)',
    lightFill: 'rgba(220, 38, 38, 0.14)',
    darkGrid: 'rgba(251, 113, 133, 0.08)',
    lightGrid: 'rgba(220, 38, 38, 0.07)',
  },
} as const;

export const StreakLineAnimation: React.FC<StreakLineAnimationProps> = ({
  variant,
  isDarkMode,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const palette = PALETTES[variant];
    const pricePoints: number[] = [];
    // Fewer, wider-spaced points preserve visible market swings instead of
    // smoothing the trace into a gentle wave.
    const maxPoints = 52;
    const lineSpeed = 0.62;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let width = 0;
    let height = 0;
    let currentPrice = 0.5;
    let momentum = 0;
    let lineOffset = 0;
    let animationFrameId = 0;
    let tickIndex = 0;

    const updatePrice = () => {
      const impulse = (Math.random() - 0.5) * 0.035;
      const tickNoise = (Math.random() - 0.5) * 0.05;
      const microPullback = (tickIndex % 2 === 0 ? 1 : -1) * (0.012 + Math.random() * 0.018);
      const priceShock = Math.random() < 0.075
        ? (Math.random() - 0.5) * 0.22
        : 0;

      tickIndex += 1;
      momentum = momentum * 0.64 + impulse;
      currentPrice += momentum + tickNoise + microPullback + priceShock + (0.5 - currentPrice) * 0.018;

      // Reflect extreme moves back into the chart instead of flattening them.
      if (currentPrice > 0.88) currentPrice = 0.88 - (currentPrice - 0.88) * 0.35;
      if (currentPrice < 0.12) currentPrice = 0.12 + (0.12 - currentPrice) * 0.35;
    };

    const seedPricePoints = () => {
      if (pricePoints.length) return;
      for (let index = 0; index < maxPoints; index += 1) {
        updatePrice();
        pricePoints.push(currentPrice);
      }
    };

    const draw = () => {
      if (!width || !height) return;
      ctx.clearRect(0, 0, width, height);

      const gridStep = Math.max(38, Math.round(height * 0.42));
      ctx.lineWidth = 0.5;
      ctx.strokeStyle = isDarkMode ? palette.darkGrid : palette.lightGrid;
      for (let index = -height; index < width; index += gridStep) {
        ctx.beginPath();
        ctx.moveTo(index, 0);
        ctx.lineTo(index + height, height);
        ctx.stroke();
      }

      const pointSpacing = width / (maxPoints - 1);
      if (!reducedMotion) {
        lineOffset += lineSpeed;
        while (lineOffset >= pointSpacing) {
          lineOffset -= pointSpacing;
          updatePrice();
          pricePoints.push(currentPrice);
          if (pricePoints.length > maxPoints) pricePoints.shift();
        }
      }

      ctx.beginPath();
      pricePoints.forEach((price, index) => {
        const x = index * pointSpacing - lineOffset;
        const y = height - price * height;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });

      ctx.lineWidth = 1.65;
      const lineGradient = ctx.createLinearGradient(0, 0, width, 0);
      palette.line.forEach((color, index) => {
        lineGradient.addColorStop(index / (palette.line.length - 1), color);
      });
      ctx.strokeStyle = lineGradient;
      ctx.lineJoin = 'miter';
      ctx.lineCap = 'butt';
      ctx.stroke();

      ctx.lineTo(width, height);
      ctx.lineTo(0, height);
      ctx.closePath();
      const fillGradient = ctx.createLinearGradient(0, height * 0.25, 0, height);
      fillGradient.addColorStop(0, isDarkMode ? palette.darkFill : palette.lightFill);
      fillGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = fillGradient;
      ctx.fill();

      if (!reducedMotion) animationFrameId = window.requestAnimationFrame(draw);
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      seedPricePoints();
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = 0;
      draw();
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    resize();

    return () => {
      resizeObserver.disconnect();
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [isDarkMode, variant]);

  return (
    <canvas
      ref={canvasRef}
      data-testid={`streak-line-${variant}`}
      data-market-line={variant}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-60"
    />
  );
};

export default StreakLineAnimation;
